<template>
    <div>
      <div class="row">
        <div class="col-lg-3">
          <div>
              <label class="form-label" for="fullName"> <span class="required">*</span> Full Name</label>
              <input
                name="amount" @input="formError.full_name = ''"
                type="text" v-model.trim="formData.full_name"
                class="form-control"
              />
              <transition name="fade">
                <span v-if="formError.full_name" class="invalid-feedback d-block qcont">{{ formError.full_name }}</span>
              </transition>
          </div>
        </div>

        <div class="col-lg-2">
          <div>
              <label class="form-label" for="mobileNo"> Mobile No (017xxxxxxxx)</label>
              <input
                  name="mobile" @input="formError.mobile_no = ''"
                  type="text" v-model.trim="formData.mobile_no"
                  class="form-control"
              />
          </div>
        </div>

        <div class="col-lg-2">
          <label class="form-label" for="gender"> <span class="required">*</span> Gender</label>
          <v-select
            placeholder="Select Gender"
            :options="formattedGenders"
            v-model="formData.gender"
            :reduce="name => name.id"
            label="name"
          />
          <transition name="fade">
            <span v-if="formError.gender" class="invalid-feedback d-block qcont">{{ formError.gender }}</span>
          </transition>
        </div>

        <div class="col-lg-3">
          <div class="d-flex justify-content-between">
              <div class="mb-1 col-4">
                <label class="form-label" for="birthday">Year</label>
                <input
                    type="text" @input="getAgeCal"
                    id="age_year" class="form-control"
                    v-model="formData.age_year"
                    placeholder="Year"
                />
              </div>
              <div class="mb-1 col-4 ml-5">
                  <label class="form-label" for="birthday">Month</label>
                  <input
                    max="12"
                    type="text" id="age_month"
                    class="form-control" @input="getAgeCal"
                    v-model="formData.age_month" placeholder="Month"
                  />
            </div>
            <div class="mb-1 col-4 ml-5">
                <label class="form-label" for="birthday">Day</label>
                <input
                  max="31" @input="getAgeCal"
                  type="text" id="age_day"
                  class="form-control"
                  v-model="formData.age_day"
                  placeholder="Day"
                />
            </div>
          </div>
        </div>

        <div v-if="! isPatientAdded || isPatientUpdated" class="col-lg-1">
          <button :disabled="loading" @click="submit" type="button" class="btn btn-primary me-1 waves-effect waves-float waves-light mt-2">
              <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              {{ isPatientUpdated ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
      <Loader v-if="loader"/>
  </div>
</template>

<script setup>
import Loader from "@/components/atom/LoaderComponent";
import handleRole from "@/services/modules/role";
import handleCompanyContact from "@/services/modules/companyContact";
import {computed, inject, onMounted, reactive, ref, watch} from "vue";
import { useRoute } from 'vue-router';
import handleHospital from "@/services/modules/hospital";

const props = defineProps(['doctors', 'onCreateProfile', 'genders', 'patientDetail', 'invoiceDate']);
const emit = defineEmits(['onCreateProfile']);
const $route = useRoute();
const loading = ref(false);
const loader = ref(false);
const { fetchCompanyDefaultRoles } = handleRole();
const { storeCompanyContact, storeAdditionalContactItems, storeCompanyContactsItems } = handleCompanyContact();
const { dateOfBarthConvertToAge } = handleHospital()
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const isPatientAdded = ref(false);
const isPatientUpdated = ref(false);

const formattedGenders = computed(() => {
  return props.genders ? Object.keys(props.genders).map(key => ({ id: key, name: props.genders[key] })) : [];
})

const formData = reactive({
  full_name: null,
  mobile_no: null,
  birthday: null,
  age_year: null,
  age_month: null,
  age_day: null,
  gender: null,
  company_rules: [],
  company_roles_data: [],
  contact_id: '',
  contact_type: 'person',
  active_tab: 'basic-info',
  profile_type: 'user',
})

const formError = reactive({
  full_name: '',
  mobile_no: '',
  birthday: '',
  gender: ''
})

const getAgeCal = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const years  = formData.age_year  || 0;
  const months = formData.age_month || 0;
  const days   = formData.age_day   || 0;

  const calculatedDate = new Date(
      today.getFullYear() - years,
      today.getMonth() - months,
      today.getDate() - days
  );

  const y = calculatedDate.getFullYear();
  const m = String(calculatedDate.getMonth() + 1).padStart(2, '0');
  const d = String(calculatedDate.getDate()).padStart(2, '0');

  formData.birthday = `${y}-${m}-${d}`;
};

watch(
    () => [props.patientDetail, props.invoiceDate],
    ([patientDetail, invoiceDate]) => {
      if (!patientDetail || !patientDetail.id) return;

      formData.full_name  = patientDetail.full_name
      formData.mobile_no  = patientDetail.mobile_no
      formData.gender     = patientDetail.gender
      formData.contact_id = patientDetail.id
      isPatientUpdated.value = true

      if (patientDetail.birthday) {
        const { year, month, day } = dateOfBarthConvertToAge(patientDetail.birthday, invoiceDate)

        formData.age_year  = year
        formData.age_month = month
        formData.age_day   = day
        formData.birthday  = patientDetail.birthday
      }
    },
    { deep: true, immediate: true }
)

const validateAgeMonth = () => {
  const maxMonth = 12;
  if (formData.age_month > maxMonth) {
    formData.age_month = maxMonth.toString();
  }
};

const validateAgeDay = () => {
  const maxDay = 31;
  if (formData.age_day > maxDay) {
    formData.age_day = maxDay.toString();
  }
};

//Functions and methods
const submit = async() => {
    if(checkValidation()) return;
    const query = getQuery();

    loading.value = true;

    await storeCompanyContact(formData, query).then((res) => {
      if (!res.status) return showError(res.message)
      const {mobile_no, referred_by, guardian_name, guardian_mobile_no, guardian_relationship} = formData
      const resData = res.data;
      saveAdditionalContactItems(res.data.id, { mobile_no, referred_by }, query)
      resData.mobile_no = mobile_no;
      resData.referred_by = referred_by;

      let item = {
        guardian_name,
        guardian_mobile_no,
        guardian_relationship
      }

      let payload = {
        contact_type: 'person',
        contact_id: res.data.id,
        active_tab: 'guardian_name',
        items: [item]
      }

      storeCompanyContactsItemsData(payload, query)
      resData.guardian_name = guardian_name;
      resData.guardian_mobile_no = guardian_mobile_no;
      resData.guardian_relationship = guardian_relationship;
      emit('onCreateProfile', resData);
      if (! isPatientUpdated.value){
        isPatientAdded.value = true;
      }
      showSuccess(res.message)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const saveAdditionalContactItems = async (contactId, keys, getQuery) => {
  await storeAdditionalContactItems(contactId, keys, getQuery).then(res => {
    loading.value = false
    if (!res.status) {
      return showError(res.message)
    }
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const storeCompanyContactsItemsData = async (payload, getQuery) => {
  await storeCompanyContactsItems(payload, getQuery).then(res => {
    loading.value = false
    if (!res.status) {
      return showError(res.message)
    }
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const checkValidation = () => {
    let flag = false;

    if(!formData.full_name){
        formError.full_name = 'Full name is required'
        flag = true;
    }

    if(!formData.gender){
        formError.gender = 'Gender is required'
        flag = true;
    }

    return flag;
}

const getQuery = () => {
  return `?company_id=${$route.params.companyId}`;
}

onMounted(() => {
  const companyQuery = `?company_id=${$route.params.companyId}&roles=patient`

  Promise.all([
    fetchCompanyDefaultRoles(companyQuery).then((res) => {
        if(res.data) {
            res.data.map((item) => {
              formData.company_rules.push(item.id);
              formData.company_roles_data.push({ id: item.id });
            });
        }
    }),
  ]).then(() => {
    loader.value = false
  }).catch((err) => {
    loader.value = false
  })

})
</script>

<style scoped>
.ml-5 {
  margin-left: 5px;
}
</style>
